import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SEARCH_PARAMS } from 'constants/search-params';
import { BackOfficeDevicesRequestParameters } from 'store/modules/backOfficeDevices/types';
import { BackOfficeEnterprisesRequestParameters } from 'store/modules/backOfficeEnterprises/types';
import { BackOfficeInvitationsRequestParameters } from 'store/modules/backOfficeInvitations/types';
import { BackOfficeUsersRequestParameters } from 'store/modules/backOfficeUsers/types';
import { FilterSetName } from 'store/modules/routerUtils/types';
import {
  type BackOfficeRequestParamsInput,
  buildBackOfficeDevicesRequestParams,
  buildBackOfficeEnterprisesRequestParams,
  buildBackOfficeInvitationsRequestParams,
  buildBackOfficeUsersRequestParams
} from 'utils/backOfficeRequestParams';

import { getFilterSetFromSearchParams } from '../drawers/BackOfficeFilterDrawer/getFilterSetFromSearchParams';

export function useBackOfficeRequestParameters<T>(
  filterSetName: FilterSetName,
  buildParams: (input: BackOfficeRequestParamsInput) => T
): T {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const currentPage = Number(searchParams.get(SEARCH_PARAMS.CURRENT_PAGE)) || undefined;
    const search = searchParams.get(SEARCH_PARAMS.SEARCH)?.trim() || undefined;
    const sortField = searchParams.get(SEARCH_PARAMS.TABLE.SORT_FIELD) || undefined;
    const sortOrder = searchParams.get(SEARCH_PARAMS.TABLE.SORT_ORDER) || undefined;
    const filterSet = getFilterSetFromSearchParams(searchParams, filterSetName);

    return buildParams({ currentPage, search, sortField, sortOrder, filterSet });
  }, [searchParams, filterSetName, buildParams]);
}

export const useBackOfficeDevicesRequestParameters = (): BackOfficeDevicesRequestParameters =>
  useBackOfficeRequestParameters(FilterSetName.BACK_OFFICE_DEVICES, buildBackOfficeDevicesRequestParams);

export const useBackOfficeEnterprisesRequestParameters = (): BackOfficeEnterprisesRequestParameters =>
  useBackOfficeRequestParameters(FilterSetName.BACK_OFFICE_ENTERPRISES, buildBackOfficeEnterprisesRequestParams);

export const useBackOfficeInvitationsRequestParameters = (): BackOfficeInvitationsRequestParameters =>
  useBackOfficeRequestParameters(FilterSetName.BACK_OFFICE_INVITATIONS, buildBackOfficeInvitationsRequestParams);

export const useBackOfficeUsersRequestParameters = (): BackOfficeUsersRequestParameters =>
  useBackOfficeRequestParameters(FilterSetName.BACK_OFFICE_USERS, buildBackOfficeUsersRequestParams);